import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Plus, Eye, Heart, Pencil, Trash2, PowerOff, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useSeo } from "../../lib/useSeo";
import { Button } from "../../components/ui/button";

const STATUS_LABEL = {
  concept: { label: "Concept", color: "bg-slate-200 text-slate-700" },
  actief: { label: "Actief", color: "bg-success/15 text-success" },
  verkocht_onder_voorbehoud: { label: "Onder voorbehoud", color: "bg-amber-200 text-amber-900" },
  verkocht: { label: "Verkocht", color: "bg-navy text-white" },
  paused: { label: "Gepauzeerd", color: "bg-slate-100 text-slate-500" },
};

const ACTIVE = new Set(["actief", "verkocht_onder_voorbehoud"]);

const VerkoopDashboard = () => {
  useSeo({ title: "Mijn woning verkopen · WoningAlert" });
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => {
    api("/particulier/sale-listings").then(setData).catch((e) => {
      toast.error(e.message);
      setData({ listings: [], max_active: 1 });
    });
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (lid, status) => {
    setBusy(lid);
    try {
      await api(`/particulier/sale-listings/${lid}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      toast.success("Bijgewerkt");
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };
  const remove = async (lid) => {
    if (!window.confirm("Advertentie definitief verwijderen?")) return;
    setBusy(lid);
    try {
      await api(`/particulier/sale-listings/${lid}`, { method: "DELETE" });
      toast.success("Verwijderd");
      load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  if (!data) return <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="animate-spin text-brandblue" /></div>;
  const listings = data.listings || [];
  const activeCount = listings.filter((l) => ACTIVE.has(l.status)).length;
  const canCreate = activeCount < (data.max_active ?? 1);

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-navy" data-testid="verkoop-title">Mijn woning verkopen</h1>
          <p className="mt-1 text-sm text-slate-500">
            Als particulier mag je 1 woning tegelijk actief hebben. Geen bemiddelingskosten, geen abonnement.
          </p>
        </div>
        {canCreate ? (
          <Button onClick={() => navigate("/mijn-woning-verkopen/nieuw")} className="rounded-xl bg-brandblue hover:bg-brandblue/90" data-testid="new-listing-btn">
            <Plus size={16} className="mr-2" /> Nieuwe advertentie
          </Button>
        ) : (
          <span className="rounded-xl bg-slate-100 px-4 py-2 text-sm text-slate-500">
            Max. {data.max_active} actieve advertentie bereikt
          </span>
        )}
      </div>

      <div className="mt-8 space-y-4">
        {listings.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-border p-12 text-center" data-testid="verkoop-empty">
            <p className="text-slate-500">Je hebt nog geen verkoop-advertentie.</p>
            <Button onClick={() => navigate("/mijn-woning-verkopen/nieuw")} className="mt-6 rounded-xl bg-brandblue hover:bg-brandblue/90">
              <Plus size={16} className="mr-2" /> Advertentie maken
            </Button>
          </div>
        ) : listings.map((l) => {
          const s = STATUS_LABEL[l.status] || STATUS_LABEL.concept;
          return (
            <div key={l.id} data-testid={`listing-${l.id}`} className="rounded-3xl border border-border bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-start gap-4">
                {l.image ? <img src={l.image} alt="" className="h-28 w-40 rounded-2xl object-cover" /> : <div className="h-28 w-40 rounded-2xl bg-slate-100" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate font-display text-lg font-bold text-navy">
                      {l.title || `${l.street || ""} ${l.house_number || ""}`.trim() || "Zonder titel"}
                    </h3>
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold ${s.color}`}>{s.label}</span>
                  </div>
                  <div className="mt-1 text-sm text-slate-500">{l.street} · {l.city}</div>
                  <div className="mt-3 flex flex-wrap gap-3 text-sm text-slate-600">
                    <span>€{Number(l.price || 0).toLocaleString("nl-NL")} {l.price_condition}</span>
                    <span>· {l.m2 || 0} m²</span>
                    <span>· {l.bedrooms || 0} slaapkamers</span>
                    <span className="inline-flex items-center gap-1"><Eye size={13} /> {l.view_count || 0}</span>
                    <span className="inline-flex items-center gap-1"><Heart size={13} /> {l.favorite_count || 0}</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => navigate(`/mijn-woning-verkopen/bewerken/${l.id}`)} className="rounded-xl" data-testid={`edit-${l.id}`}>
                  <Pencil size={14} className="mr-1.5" /> Bewerken
                </Button>
                {l.status !== "actief" && (
                  <Button variant="outline" onClick={() => setStatus(l.id, "actief")} disabled={busy === l.id} className="rounded-xl" data-testid={`activate-${l.id}`}>
                    <CheckCheck size={14} className="mr-1.5" /> Publiceren
                  </Button>
                )}
                {l.status === "actief" && (
                  <Button variant="outline" onClick={() => setStatus(l.id, "paused")} disabled={busy === l.id} className="rounded-xl">
                    <PowerOff size={14} className="mr-1.5" /> Pauzeren
                  </Button>
                )}
                {ACTIVE.has(l.status) && (
                  <Button variant="outline" onClick={() => setStatus(l.id, "verkocht")} disabled={busy === l.id} className="rounded-xl" data-testid={`sold-${l.id}`}>
                    Markeer als verkocht
                  </Button>
                )}
                <Button variant="outline" onClick={() => remove(l.id)} disabled={busy === l.id} className="rounded-xl text-danger hover:bg-danger/5" data-testid={`delete-${l.id}`}>
                  <Trash2 size={14} className="mr-1.5" /> Verwijderen
                </Button>
                {l.slug && (
                  <Link to={`/te-koop/${l.slug}`} className="ml-auto inline-flex items-center gap-1 self-center text-sm font-semibold text-brandblue hover:underline">
                    Bekijk publiek →
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!user && (
        <p className="mt-10 text-center text-sm text-slate-500">
          <Link to="/login" className="font-semibold text-brandblue">Inloggen</Link> of{" "}
          <Link to="/register" className="font-semibold text-brandblue">account aanmaken</Link> om te starten.
        </p>
      )}
    </div>
  );
};

export default VerkoopDashboard;
